'use client';
import { StatsigOnDeviceEvalClient } from '@statsig/js-on-device-eval-client';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { buildStatsigUser } from './user';

import type { DynamicConfigName, Feature, StatsigTier } from './flags';
import type { StatsigCustomFields, StatsigFieldOverrides, StatsigUserShape } from './user';
import type { FC, ReactElement, ReactNode } from 'react';

export type StatsigImpressionListener = (evaluation: { feature: string; value: boolean }) => void;

export type { DynamicConfigName, Feature, StatsigTier } from './flags';
export type { StatsigFieldOverrides } from './user';

type App = 'clients' | 'sessions';

type Props = {
  app: App;
  attributes?: { demo?: boolean };
  statsigClientKey?: string;
  environment?: StatsigTier;
  children: ((args: { isReady: boolean }) => ReactNode) | ReactNode;
  // Receives one call per gate evaluation, ready to forward to an analytics sink.
  statsigImpressionListener?: StatsigImpressionListener;
};

// Holds the active on-device Statsig client so useFeatureFlags can evaluate gates against a
// per-call user. The on-device client downloads the full ruleset once and evaluates any user
// object synchronously — so there is no global identity to mutate.
const StatsigClientCtx = createContext<StatsigOnDeviceEvalClient | null>(null);

// Lowest-priority fields seeded by the provider so every evaluation — including anonymous,
// pre-login ones — carries app/demo/hostname. Per-user and per-call fields layer on top.
const StatsigBaseCtx = createContext<Pick<StatsigCustomFields, 'app' | 'demo' | 'hostname'> | null>(null);

type UserContext = { userId?: string; fields: StatsigFieldOverrides };

const Ctx = createContext<UserContext | undefined>(undefined);

type UserFeatureFlagProviderProps = {
  children: (args: { isReady: boolean }) => ReactElement;
  userId?: string;
  userAttributes?: Pick<StatsigFieldOverrides, 'team_id' | 'type' | 'study_id'>;
};

// Publishes the per-user context that useFeatureFlags evaluates gates against. The on-device
// client needs no per-user sync — it evaluates whatever user object it's handed — so there's no
// per-user readiness to await here; the ruleset download is gated once by FeatureFlagsProvider.
export const UserFeatureFlagProvider: FC<UserFeatureFlagProviderProps> = ({ children, userAttributes, userId }) => {
  const teamId = userAttributes?.team_id;
  const type = userAttributes?.type;
  const studyId = userAttributes?.study_id;
  const value = useMemo(
    () => ({ userId, fields: { team_id: teamId, type, study_id: studyId } }),
    [userId, teamId, type, studyId],
  );

  return <Ctx.Provider value={value}>{children({ isReady: true })}</Ctx.Provider>;
};

// Layering, rather than letting `att` replace the bound user, keeps a partial per-call `att`
// (e.g. just { team_id } in a room) from dropping bound fields like type. Shared by gate and
// dynamic-config reads so both target on the same user.
function useStatsigUser(att?: StatsigFieldOverrides): StatsigUserShape {
  const userDetails = useContext(Ctx);
  const baseFields = useContext(StatsigBaseCtx);
  return useMemo(
    () => buildStatsigUser(userDetails?.userId, baseFields, userDetails?.fields, att),
    [att, userDetails, baseFields],
  );
}

function useFeatureFlags<T extends Feature = Feature>(flags: T[], att?: StatsigFieldOverrides): Record<T, boolean> {
  const statsigClient = useContext(StatsigClientCtx);
  const statsigUser = useStatsigUser(att);

  return useMemo(() => {
    const statsigReady = statsigClient?.loadingStatus === 'Ready';

    return flags.reduce<Record<T, boolean>>(
      (acc, flag) => {
        // Only evaluate against a loaded ruleset; an unready client returns NoValues (false) for
        // every gate, so checking it would silently disable the flag and log a misleading exposure.
        acc[flag] = statsigClient && statsigReady ? statsigClient.checkGate(flag, statsigUser) : false;
        return acc;
      },
      {} as Record<T, boolean>,
    );
  }, [flags, statsigClient, statsigUser]);
}

// Reads a Statsig dynamic config's JSON value for a per-call user, mirroring useFeatureFlags'
// field layering (so a room can target it by { study_id, team_id } the same way it targets gates).
// Returns undefined until the ruleset is loaded — an unready on-device client returns an empty
// config, which we surface as "no value" so callers keep their own defaults rather than treating
// not-ready as an empty override. Logs a config exposure by default, like the gates do.
function useDynamicConfig(
  configName: DynamicConfigName,
  att?: StatsigFieldOverrides,
): Record<string, unknown> | undefined {
  const statsigClient = useContext(StatsigClientCtx);
  const statsigUser = useStatsigUser(att);

  return useMemo(() => {
    const statsigReady = statsigClient?.loadingStatus === 'Ready';
    if (!statsigClient || !statsigReady) {
      return undefined;
    }
    return statsigClient.getDynamicConfig(configName, statsigUser).value;
  }, [configName, statsigClient, statsigUser]);
}

// Owns the on-device client lifecycle. Hooks must be unconditional, so this lives in its own
// subcomponent that's only mounted when a clientKey is provided — the "no key" case is a graceful
// no-op where every gate reads false.
function StatsigSection({
  sdkKey,
  tier,
  impressionListener,
  children,
}: {
  sdkKey: string;
  tier?: StatsigTier;
  impressionListener?: StatsigImpressionListener;
  children: (statsigReady: boolean) => ReactNode;
}) {
  const [client] = useState(() => new StatsigOnDeviceEvalClient(sdkKey, tier ? { environment: { tier } } : null));
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!impressionListener) return;
    const handler = (event: { gate: { name: string; value: boolean } }) => {
      impressionListener({ feature: event.gate.name, value: event.gate.value });
    };
    client.on('gate_evaluation', handler);
    return () => client.off('gate_evaluation', handler);
  }, [client, impressionListener]);

  // Downloads the ruleset once; afterwards every checkGate is synchronous for any user.
  // initializeAsync resolves (not rejects) on a failed download with `success: false` — surface
  // that loudly, because an unloaded ruleset evaluates every gate to false. Mark ready regardless
  // so a Statsig outage can't block the app: gates degrade to false (gated on loadingStatus in
  // useFeatureFlags) rather than hanging on the loading screen.
  useEffect(() => {
    let active = true;
    client
      .initializeAsync()
      .then(result => {
        if (!result.success) {
          console.error('[feature-flags] Statsig ruleset failed to load — gates will read false', result.error);
        }
      })
      .catch(err => {
        console.error('[feature-flags] Statsig initializeAsync threw', err);
      })
      .finally(() => {
        if (active) setIsReady(true);
      });
    return () => {
      active = false;
    };
  }, [client]);

  return <StatsigClientCtx.Provider value={client}>{children(isReady)}</StatsigClientCtx.Provider>;
}

const FeatureFlagsProvider = ({
  children,
  statsigClientKey,
  environment,
  app,
  attributes,
  statsigImpressionListener,
}: Props) => {
  const baseFields = useMemo(
    () => ({
      app,
      demo: attributes?.demo,
      hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
    }),
    [app, attributes?.demo],
  );

  return (
    <StatsigBaseCtx.Provider value={baseFields}>
      {statsigClientKey ? (
        <StatsigSection sdkKey={statsigClientKey} tier={environment} impressionListener={statsigImpressionListener}>
          {statsigReady => (typeof children === 'function' ? children({ isReady: statsigReady }) : children)}
        </StatsigSection>
      ) : (
        <>{typeof children === 'function' ? children({ isReady: true }) : children}</>
      )}
    </StatsigBaseCtx.Provider>
  );
};

export { FeatureFlagsProvider, useDynamicConfig, useFeatureFlags };
